package com.yogaaicoach.pages.insights

import com.yogaaicoach.common.ui.components.LoadingPage
import com.yogaaicoach.common.ui.components.TopBar
import com.yogaaicoach.pages.insights.data.ChartData
import com.yogaaicoach.pages.insights.data.InsightsStats
import com.yogaaicoach.scope
import com.yogaaicoach.user.AppViewModel
import kotlinx.coroutines.launch
import react.ChildrenBuilder
import react.FC
import react.Props
import react.dom.html.ReactHTML.button
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.p
import react.dom.html.ReactHTML.span
import react.dom.svg.ReactSVG.path
import react.dom.svg.ReactSVG.svg
import react.useEffectOnce
import react.useState
import web.cssom.ClassName
import kotlin.js.json
import kotlin.math.abs

internal external interface InsightsPageProps : Props {
    var viewModel: InsightsViewModel
    var appViewModel: AppViewModel
    var onNavigateToDashboard: () -> Unit
    var onNavigateToPayment: () -> Unit
    var onNavigateToContactUs: () -> Unit
}

internal val InsightsPage = FC<InsightsPageProps> { props ->
    var isLoaded by useState(false)
    var stats by useState<InsightsStats?>(null)
    var chartData by useState<ChartData?>(null)
    var weeklySummaries by useState<List<WeekSummary>>(emptyList())
    var selectedBarIndex by useState<Int?>(null)
    var viewMode by useState(ChartViewMode.Monthly)
    var periodOffset by useState(0)

    useEffectOnce {
        scope.launch { props.viewModel.isLoaded.collect { isLoaded = it } }
        scope.launch { props.viewModel.stats.collect { stats = it } }
        scope.launch { props.viewModel.chartData.collect { chartData = it } }
        scope.launch { props.viewModel.weeklySummaries.collect { weeklySummaries = it } }
        props.viewModel.loadPage()
    }

    TopBar {
        title = "Insights"
        onSignOut = { props.appViewModel.signOut() }
        onNavigateToDashboard = props.onNavigateToDashboard
        onNavigateToInsights = null
        onNavigateToPayment = props.onNavigateToPayment
        onNavigateToContactUs = props.onNavigateToContactUs
    }

    if (!isLoaded) {
        LoadingPage {}
        return@FC
    }

    div {
        className = ClassName("insights-page-shell")
        onClick = { selectedBarIndex = null }

        p { className = ClassName("insights-page-title"); +"Your Progress" }

        stats?.let { s ->
            val cards = statsCards(s)
            // Small screen: 2×2 grid
            div {
                className = ClassName("insights-stat-grid-small")
                div {
                    className = ClassName("insights-stat-row")
                    InsightsStatCard(cards[0].title, cards[0].value, cards[0].subtitle, 0, cards[0].iconColor.toCssHex())
                    InsightsStatCard(cards[1].title, cards[1].value, cards[1].subtitle, 1, cards[1].iconColor.toCssHex())
                }
                div {
                    className = ClassName("insights-stat-row")
                    InsightsStatCard(cards[2].title, cards[2].value, cards[2].subtitle, 2, cards[2].iconColor.toCssHex())
                    InsightsStatCard(cards[3].title, cards[3].value, cards[3].subtitle, 3, cards[3].iconColor.toCssHex())
                }
            }
            // Large screen: 1×4 row
            div {
                className = ClassName("insights-stat-grid-large")
                cards.forEachIndexed { i, card ->
                    InsightsStatCard(card.title, card.value, card.subtitle, i, card.iconColor.toCssHex())
                }
            }
        }

        InsightsChartCard(
            chartData = chartData,
            selectedBarIndex = selectedBarIndex,
            viewMode = viewMode,
            periodOffset = periodOffset,
            onViewModeChange = { mode -> viewMode = mode; periodOffset = 0; selectedBarIndex = null },
            onPeriodOffsetChange = { delta -> periodOffset += delta; selectedBarIndex = null },
            onBarSelect = { i -> selectedBarIndex = if (selectedBarIndex == i) null else i },
        )

        if (weeklySummaries.isNotEmpty()) {
            InsightsWeeklySummaries(weeklySummaries)
        }
    }
}

// ── Color helper ─────────────────────────────────────────────────────────────

private fun androidx.compose.ui.graphics.Color.toCssHex(): String {
    val r = (red * 255).toInt()
    val g = (green * 255).toInt()
    val b = (blue * 255).toInt()
    return "#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}"
}

// ── Stat cards ────────────────────────────────────────────────────────────────

private fun ChildrenBuilder.InsightsStatCard(
    title: String,
    value: String,
    subtitle: String,
    iconIndex: Int,
    color: String,
) {
    div {
        className = ClassName("insights-stat-card")
        div {
            className = ClassName("insights-stat-card-header")
            div {
                className = ClassName("insights-stat-icon-box")
                style = json("background" to "${color}1f") as react.CSSProperties
                StatIconSvg(iconIndex, color)
            }
            span {
                className = ClassName("insights-stat-title")
                +title.uppercase()
            }
        }
        p { className = ClassName("insights-stat-value"); +value }
        p { className = ClassName("insights-stat-subtitle"); +subtitle }
    }
}

// Icon SVG paths for stat cards (Material Icons outlined variants)
private val statIconPaths = listOf(
    // 0: AccessTime
    listOf(
        "M11.99,2C6.47,2 2,6.48 2,12s4.47,10 9.99,10C17.52,22 22,17.52 22,12S17.52,2 11.99,2zM12,20c-4.42,0 -8,-3.58 -8,-8s3.58,-8 8,-8 8,3.58 8,8 -3.58,8 -8,8z",
        "M12.5,7H11v6l5.25,3.15 0.75,-1.23 -4.5,-2.67z",
    ),
    // 1: FitnessCenter
    listOf("M20.57,14.86L22,13.43 20.57,12 17,15.57 8.43,7 12,3.43 10.57,2 9.14,3.43 7.71,2 5.57,4.14 4.14,2.71 2.71,4.14l1.43,1.43L2,7.71l1.43,1.43L2,10.57 3.43,12 7,8.43 15.57,17 12,20.57 13.43,22l1.43,-1.43L16.29,22l2.14,-2.14 1.43,1.43 1.43,-1.43 -1.43,-1.43L22,16.29z"),
    // 2: TrendingUp
    listOf("M16,6l2.29,2.29 -4.88,4.88 -4,-4L2,16.59 3.41,18l6,-6 4,4 6.3,-6.29L22,12V6z"),
    // 3: EmojiEvents
    listOf("M19,5h-2V3H7v2H5C3.9,5 3,5.9 3,7v1c0,2.55 1.92,4.63 4.39,4.94 0.63,1.5 1.98,2.63 3.61,2.96V19H7v2h10v-2h-4v-3.1c1.63,-0.33 2.98,-1.46 3.61,-2.96C19.08,12.63 21,10.55 21,8V7c0,-1.1 -0.9,-2 -2,-2z M5,8V7h2v3.82C5.86,10.4 5,9.3 5,8z M19,8c0,1.3 -0.86,2.4 -2,2.82V7h2v1z M12,15c-1.65,0 -3,-1.35 -3,-3V5h6v7c0,1.65 -1.35,3 -3,3z"),
)

private fun ChildrenBuilder.StatIconSvg(iconIndex: Int, fill: String) {
    svg {
        width = 16.0; height = 16.0; viewBox = "0 0 24 24"
        statIconPaths[iconIndex].forEach { pathData ->
            path { this.fill = fill; d = pathData }
        }
    }
}

// ── Chart card ────────────────────────────────────────────────────────────────

private fun ChildrenBuilder.InsightsChartCard(
    chartData: ChartData?,
    selectedBarIndex: Int?,
    viewMode: ChartViewMode,
    periodOffset: Int,
    onViewModeChange: (ChartViewMode) -> Unit,
    onPeriodOffsetChange: (Int) -> Unit,
    onBarSelect: (Int) -> Unit,
) {
    val monthly = chartData?.monthly ?: emptyList()
    val weekly = chartData?.weekly ?: emptyList()
    val bars = if (viewMode == ChartViewMode.Monthly) monthlyBars(monthly, periodOffset)
               else weeklyBars(weekly, periodOffset)
    val maxOffset = if (viewMode == ChartViewMode.Monthly) monthlyMaxPageOffset(monthly)
                   else weeklyMaxPageOffset(weekly)
    val label = periodLabel(viewMode, monthly, weekly, periodOffset)
    val niceMaxVal = niceMax(bars.maxOfOrNull { it.minutes } ?: 0)

    div {
        className = ClassName("insights-card")
        onClick = { it.stopPropagation() }

        // Header row: title + toggle
        div {
            className = ClassName("insights-chart-header")
            p { className = ClassName("insights-chart-title"); +"Training Minutes" }
            ChartViewToggle(viewMode, onViewModeChange)
        }

        // Period navigation row
        div {
            className = ClassName("insights-chart-nav")
            button {
                className = ClassName(if (periodOffset < maxOffset) "insights-chart-nav-btn" else "insights-chart-nav-btn insights-chart-nav-btn-disabled")
                disabled = periodOffset >= maxOffset
                onClick = { it.stopPropagation(); onPeriodOffsetChange(1) }
                ChevronSvg(left = true)
            }
            span { className = ClassName("insights-chart-period-label"); +label }
            button {
                className = ClassName(if (periodOffset > 0) "insights-chart-nav-btn" else "insights-chart-nav-btn insights-chart-nav-btn-disabled")
                disabled = periodOffset <= 0
                onClick = { it.stopPropagation(); onPeriodOffsetChange(-1) }
                ChevronSvg(left = false)
            }
        }

        // Bar chart
        InsightsBarChart(bars, niceMaxVal, selectedBarIndex, onBarSelect)
    }
}

private fun ChildrenBuilder.ChartViewToggle(selected: ChartViewMode, onSelect: (ChartViewMode) -> Unit) {
    div {
        className = ClassName("insights-chart-toggle")
        ChartViewMode.entries.forEach { mode ->
            button {
                className = ClassName(if (mode == selected) "insights-chart-toggle-btn insights-chart-toggle-btn-selected" else "insights-chart-toggle-btn")
                onClick = { it.stopPropagation(); onSelect(mode) }
                +(if (mode == ChartViewMode.Monthly) "Monthly" else "Weekly")
            }
        }
    }
}

private fun ChildrenBuilder.ChevronSvg(left: Boolean) {
    svg {
        width = 18.0; height = 18.0; viewBox = "0 0 24 24"
        path {
            fill = "currentColor"
            d = if (left) "M15.41,16.59L10.83,12l4.58,-4.59L14,6l-6,6 6,6z"
            else "M8.59,16.59L13.17,12 8.59,7.41 10,6l6,6 -6,6z"
        }
    }
}

private fun ChildrenBuilder.InsightsBarChart(
    bars: List<com.yogaaicoach.pages.insights.ChartBarData>,
    niceMaxVal: Int,
    selectedIndex: Int?,
    onBarSelect: (Int) -> Unit,
) {
    val chartHeight = 140  // px

    div {
        className = ClassName("insights-chart-area")
        onClick = { it.stopPropagation() }

        // Y-axis + bars row
        div {
            className = ClassName("insights-chart-row")

            // Y-axis labels
            div {
                className = ClassName("insights-y-axis")
                listOf(1f, 0.75f, 0.5f, 0.25f).forEach { fraction ->
                    span {
                        className = ClassName("insights-y-label")
                        style = json("top" to "${((1f - fraction) * chartHeight).toInt()}px") as react.CSSProperties
                        +(niceMaxVal * fraction).toInt().toString()
                    }
                }
            }

            // Bars + grid area
            div {
                className = ClassName("insights-bars-container")

                // Grid lines
                listOf(0.25f, 0.5f, 0.75f).forEach { fraction ->
                    div {
                        className = ClassName("insights-grid-line")
                        style = json("bottom" to "${(fraction * chartHeight).toInt()}px") as react.CSSProperties
                    }
                }

                // Bars
                bars.forEachIndexed { i, bar ->
                    val isDimmed = selectedIndex != null && selectedIndex != i
                    val heightPx = if (niceMaxVal > 0) (bar.minutes.toFloat() / niceMaxVal * chartHeight).toInt() else 0

                    div {
                        className = ClassName("insights-bar-col")
                        onClick = { e ->
                            e.stopPropagation()
                            if (bar.minutes > 0) onBarSelect(i)
                        }

                        // Tooltip
                        if (selectedIndex == i) {
                            div {
                                className = ClassName("insights-tooltip")
                                style = json("bottom" to "${heightPx + 10}px") as react.CSSProperties
                                p { className = ClassName("insights-tooltip-label"); +bar.tooltipLabel }
                                p { className = ClassName("insights-tooltip-value"); +"${bar.minutes} min" }
                                div { className = ClassName("insights-tooltip-caret") }
                            }
                        }

                        // Bar or stub
                        if (bar.minutes > 0) {
                            div {
                                className = ClassName(if (isDimmed) "insights-bar insights-bar-dimmed" else "insights-bar")
                                style = json("height" to "${heightPx}px") as react.CSSProperties
                            }
                        } else {
                            div { className = ClassName("insights-bar-stub") }
                        }
                    }
                }
            }
        }

        // X-axis labels
        div {
            className = ClassName("insights-x-axis")
            div { className = ClassName("insights-x-axis-spacer") }
            div {
                className = ClassName("insights-x-labels")
                bars.forEach { bar ->
                    span { className = ClassName("insights-x-label"); +bar.label }
                }
            }
        }
    }
}

// ── Weekly summaries ──────────────────────────────────────────────────────────

private val focusAreaPalette = listOf(
    "#0096c7", "#00b4a2", "#7b61ff", "#e8a020",
    "#e05c2a", "#2a7d4e", "#9c6ade", "#d4495a",
    "#4a90d9", "#5c8a3c",
)

private fun focusAreaColor(focusArea: String): String =
    focusAreaPalette[abs(focusArea.hashCode()) % focusAreaPalette.size]

private fun ChildrenBuilder.InsightsWeeklySummaries(summaries: List<WeekSummary>) {
    div {
        className = ClassName("insights-weekly-section")
        p { className = ClassName("insights-weekly-title"); +"Weekly Summaries" }
        summaries.forEachIndexed { i, week ->
            WeekSummaryCard(week, i)
        }
    }
}

private fun ChildrenBuilder.WeekSummaryCard(week: WeekSummary, index: Int) {
    val isCurrentWeek = index == 0
    div {
        className = ClassName("insights-card insights-week-card")

        // Primary accent bar for current week
        if (isCurrentWeek) {
            div { className = ClassName("insights-week-accent-bar") }
        }

        // Stats pane (small: full-width header; large: left column)
        div {
            className = ClassName("insights-week-stats")
            WeekStatsContent(week, index, isCurrentWeek)
        }

        div { className = ClassName("insights-week-divider") }

        // Workouts pane
        div {
            className = ClassName("insights-week-workouts")
            span { className = ClassName("insights-workouts-label"); +"WORKOUTS" }
            week.workouts.forEach { WorkoutRow(it) }
        }
    }
}

private fun ChildrenBuilder.WeekStatsContent(week: WeekSummary, index: Int, isCurrentWeek: Boolean) {
    // Relative week pill
    if (isCurrentWeek) {
        span { className = ClassName("insights-week-pill insights-week-pill-current"); +relativeWeekLabel(index) }
    } else {
        span { className = ClassName("insights-week-pill"); +relativeWeekLabel(index) }
    }
    span { className = ClassName("insights-week-date"); +week.weekLabel }

    // Change percent badge
    week.changePercent?.let { pct ->
        val positive = pct >= 0
        span {
            className = ClassName(if (positive) "insights-change-badge insights-change-badge-pos" else "insights-change-badge insights-change-badge-neg")
            +"${if (positive) "↑" else "↓"} ${abs(pct)}% vs prior"
        }
    }

    p { className = ClassName("insights-week-total"); +formatMinutes(week.totalMinutes) }

    div {
        className = ClassName("insights-week-meta")
        div {
            className = ClassName("insights-week-meta-item")
            FitnessIconSmall()
            span { +week.sessionLabel }
        }
        div {
            className = ClassName("insights-week-meta-item")
            ScheduleIconSmall()
            span { +"~${week.avgSessionMin} min avg" }
        }
    }
}

private fun ChildrenBuilder.WorkoutRow(workout: WorkoutEntry) {
    val color = focusAreaColor(workout.focusArea)
    div {
        className = ClassName("insights-workout-row")
        style = json("background" to "${color}0f") as react.CSSProperties
        div {
            className = ClassName("insights-workout-dot")
            style = json("background" to color) as react.CSSProperties
        }
        span { className = ClassName("insights-workout-day"); +workout.weekday }
        span { className = ClassName("insights-workout-area"); +workout.focusArea }
        span {
            className = ClassName("insights-workout-dur")
            style = json("color" to color) as react.CSSProperties
            +"${workout.durationMin}m"
        }
    }
}

private fun ChildrenBuilder.FitnessIconSmall() {
    svg {
        width = 12.0; height = 12.0; viewBox = "0 0 24 24"
        path {
            fill = "currentColor"
            d = "M20.57,14.86L22,13.43 20.57,12 17,15.57 8.43,7 12,3.43 10.57,2 9.14,3.43 7.71,2 5.57,4.14 4.14,2.71 2.71,4.14l1.43,1.43L2,7.71l1.43,1.43L2,10.57 3.43,12 7,8.43 15.57,17 12,20.57 13.43,22l1.43,-1.43L16.29,22l2.14,-2.14 1.43,1.43 1.43,-1.43 -1.43,-1.43L22,16.29z"
        }
    }
}

private fun ChildrenBuilder.ScheduleIconSmall() {
    svg {
        width = 12.0; height = 12.0; viewBox = "0 0 24 24"
        path { fill = "currentColor"; d = "M11.99,2C6.47,2 2,6.48 2,12s4.47,10 9.99,10C17.52,22 22,17.52 22,12S17.52,2 11.99,2zM12,20c-4.42,0 -8,-3.58 -8,-8s3.58,-8 8,-8 8,3.58 8,8 -3.58,8 -8,8z" }
        path { fill = "currentColor"; d = "M12.5,7H11v6l5.25,3.15 0.75,-1.23 -4.5,-2.67z" }
    }
}

// ── Helpers ───────────────────────────────────────────────────────────────────

private fun relativeWeekLabel(index: Int): String = when (index) {
    0 -> "This Week"
    1 -> "Last Week"
    else -> "$index Weeks Ago"
}

private fun formatMinutes(minutes: Int): String {
    if (minutes < 60) return "${minutes}m"
    val h = minutes / 60
    val m = minutes % 60
    return if (m == 0) "${h}h" else "${h}h ${m}m"
}
